import { type ReactNode, createContext, useEffect, useState } from 'react';
import * as Sentry from '@sentry/nextjs';
import { AuthStatus } from 'constants/auth';
import { RequestStatus } from 'constants/requests';
import { type GetCurrentUserQuery, useGetCurrentUserLazyQuery } from 'types/generated/client';
import { identify } from 'services/client/analytics';
import { useGeo } from 'context/GeoContext';
import useViewer from 'hooks/useViewer';

interface GeoView {
  ip?: string;
  city?: string;
  region?: string;
  country?: string;
  requestStatus: RequestStatus;
}

export type CurrentUserResponse =
  | (ReturnType<typeof useGetCurrentUserLazyQuery>[1] & {
      user?: GetCurrentUserQuery['users_by_pk'] | null;
    })
  | null;
interface CurrentUser {
  currentUser: CurrentUserResponse;
  geo: null | GeoView;
}

const DEFAULT_USER: CurrentUser = {
  currentUser: null,
  geo: null,
};

export const CurrentUserContext = createContext(DEFAULT_USER);

export const CurrentUserProvider = ({ children }: { children: ReactNode }) => {
  const session = useViewer();
  const userId = session.viewer?.uid;
  // Geo comes from the one app-wide lookup (GeoProvider) instead of a second /api/v1/ip
  // fetch this context used to fire on every mount for a value nothing read.
  const geoLookup = useGeo();
  const [hasIdentifiedUser, setHasIdentifiedUser] = useState(false);

  const [getCurrentUserLazyQuery, queryResult] = useGetCurrentUserLazyQuery();
  const { data } = queryResult;

  useEffect(() => {
    if (data?.users_by_pk?.id && !hasIdentifiedUser) {
      setHasIdentifiedUser(true);
      try {
        const user = data.users_by_pk;
        const { ...userAttributes } = user;
        identify({
          ...userAttributes,
          email: user.email,
          name: `${user.firstName || ''} ${user.lastName || ''}`.trim(),
          userId: user.id,
          additionalUserParams: {
            ...userAttributes,
          },
        });
      } catch (error) {
        Sentry.captureException(error);
      }
      try {
        // @ts-ignore should be defined
        gtag('set', 'user_properties', { user_id: data.users_by_pk.id });
      } catch (error) {
        Sentry.captureException(error);
      }
    }
  }, [data, hasIdentifiedUser]);

  useEffect(() => {
    if (!!userId && session.status === AuthStatus.User) {
      /**
       * @todo [LAUNCH]: logout issue?
       */
      // NOTE: Should we check for firebase token here?
      // There are some edge race conditions where it's fetching without a token, but it seems rare and to not effect usability.
      getCurrentUserLazyQuery({ variables: { id: userId } });
    }
  }, [session.status, userId, getCurrentUserLazyQuery]);

  // NOTE: This ensures we only return the current user if the session exists from firebase.
  // It is possible the current user could remain in the apollo cache and won't update until the useEffect above runs.
  const currentUser = { ...queryResult, user: userId ? data?.users_by_pk : null };

  const requestStatus =
    geoLookup.status === 'loading'
      ? RequestStatus.InProgress
      : geoLookup.status === 'success'
      ? RequestStatus.Success
      : RequestStatus.Error;
  const geo: GeoView = {
    ip: geoLookup.ip ?? undefined,
    city: geoLookup.city ?? undefined,
    region: geoLookup.region ?? undefined,
    country: geoLookup.country ?? undefined,
    requestStatus,
  };

  return (
    <CurrentUserContext.Provider
      value={{
        currentUser,
        geo,
      }}
    >
      {children}
    </CurrentUserContext.Provider>
  );
};
